import React from "react";
import PhoneInput, { type Value } from "react-phone-number-input";
import cn from "classnames";

import styles from "./TelephoneField.module.scss";
import "react-phone-number-input/style.css";
const telRegex = /^\+\d{7,15}(?:x.+)?$/i;

interface FieldWrapperProps {
    defaultValue: Value;
    fieldErrors?: string[];
    label?: string;
    id?: string;
    className?: string;
    onChange?: (value?: Value) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    inputClassName?: string;
    outlineClassName?: string;
    errorClassName?: string;
    labelClassName?: string;
}

type CustomInput = Omit<HTMLInputElement, "onChange" | "labels" | "ref" | "defaultValue">;
interface BaseFieldProps extends FieldWrapperProps, Partial<CustomInput> {}

/**
 * Generates a telephone field, including label and errors.
 */
const TelephoneField = (props: BaseFieldProps): React.ReactElement => {
    const {
        id,
        label,
        className,
        inputClassName,
        outlineClassName,
        errorClassName,
        labelClassName,
        fieldErrors = [],
        required = false,
        placeholder = "e.g. 1 212 345-5673",
        onChange = () => { },
        onBlur = () => { },
        defaultValue,
        ...inputProps
    } = props;

    const hasErrors = fieldErrors.length > 0;
    const inputGroupClassNames = cn(
        styles.inputGroup,
        hasErrors ? cn(styles.groupError, errorClassName) : "",
    );

    const labelClassNames = cn(styles.label, labelClassName, hasErrors ? styles.labelError : "");

    return (
        <span className={cn(styles.field, className)}>
            <label htmlFor={id} className={labelClassNames}>
                {label}{required ? "*" : ""}
            </label>
            <span className={inputGroupClassNames}>
                <PhoneInput
                    {...inputProps}
                    aria-label={hasErrors ? fieldErrors.join(" ") : label}
                    aria-required={required}
                    autoCapitalize="off"
                    autoComplete="tel"
                    autoCorrect="off"
                    className={cn(styles.input, inputClassName)}
                    countryCallingCodeEditable={false}
                    countryOptionsOrder={["US", "CA", "|", "..."]}
                    countrySelectProps={{ unicodeFlags: true }}
                    defaultCountry="US"
                    id={id ?? "phone"}
                    international={true}
                    initialValueFormat="national"
                    limitMaxLength={true}
                    name={id ?? "phone"}
                    onBlur={onBlur}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    spellCheck={false}
                    type="tel"
                    value={defaultValue}
                />
                <span className={cn(styles.outline, outlineClassName)}></span>
            </span>
            <span className={styles.error}>
                {fieldErrors.map((error) => (
                    <span key={error} className={cn(styles.error, styles.fieldError)}>
                        Error: {error}
                    </span>
                ))}
            </span>
        </span>
    );
}

export default TelephoneField;
export { telRegex };
export type { BaseFieldProps, FieldWrapperProps };
